import React, { useState } from 'react';
import type { FC } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { isIncreasedFlexibilityEnabled } from '@helpcenter/feature-flags';
import * as fonts from '@helpcenter/styles/src/fonts';
import { Title } from '@helpcenter/styles/src/help-center';
import { ThemedButton } from '@helpcenter/themed-button';
import FallbackPortals from 'assets/images/fallback-portals.svg';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { ElementRoot, ElementWidth } from 'view/common/element-root';
import messages from 'view/help-center/featured-and-sorted-portals-card-list/messages';
import { HelpCenterSinglePortal } from 'view/help-center/help-center-content/single-portal';
import { useIsPageInEditMode, useIsPageInPreviewMode } from 'view/layout-next/controllers';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import EmptyState from '@atlaskit/empty-state';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';
import { Stack, Inline, xcss, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type {
    listOfPortalsOnHelpCenter_HelpLayoutListOfHelpCenterPortalsAtomicElement$key as ListOfPortalsElement,
    listOfPortalsOnHelpCenter_HelpLayoutListOfHelpCenterPortalsAtomicElement$data as Data,
} from './__generated__/listOfPortalsOnHelpCenter_HelpLayoutListOfHelpCenterPortalsAtomicElement.graphql';
import fallbackMessage from './messages';
import { Portal } from './portal/portal';

type Portal = NonNullable<Data['portals'][number]>;

export const ListOfPortals: FC<{
    listOfHelpCenters: ListOfPortalsElement;
    isSidebarOpen: boolean;
}> = ({ listOfHelpCenters, isSidebarOpen }) => {
    const [areAllPortalsVisible, setAllPortalsVisibility] = useState(false);
    const { formatMessage } = useIntl();
    const { portals, visualConfig, expandButtonTextColor } = useFragment(
        graphql`
            fragment listOfPortalsOnHelpCenter_HelpLayoutListOfHelpCenterPortalsAtomicElement on HelpLayoutPortalsListElement {
                portals @required(action: THROW) {
                    ...portal_HelpCenterPortal
                    isFeatured
                    portalId
                }
                expandButtonTextColor
                visualConfig {
                    titleColor
                }
            }
        `,
        listOfHelpCenters
    );
    di(useIsPageInEditMode);
    di(useIsPageInPreviewMode);
    di(isIncreasedFlexibilityEnabled);

    const toggleExpandedView = () => setAllPortalsVisibility(!areAllPortalsVisible);
    const [isPageInEditMode] = useIsPageInEditMode();
    const [isPageInPreviewMode] = useIsPageInPreviewMode();

    const { featuredPortals, allOtherPortals } = portals.reduce<{
        featuredPortals: Portal[];
        allOtherPortals: Portal[];
    }>(
        (acc, current) => {
            if (current) {
                if (current.isFeatured) acc.featuredPortals.push(current);
                else acc.allOtherPortals.push(current);
            }
            return acc;
        },
        {
            featuredPortals: [],
            allOtherPortals: [],
        }
    );
    if (portals.length === 0 && isPageInEditMode && !isPageInPreviewMode) {
        const message = isIncreasedFlexibilityEnabled()
            ? fallbackMessage.emptyStatePortalsDescription
            : fallbackMessage.emptyStateDescription;
        return (
            <Box xcss={nonDraggableStyles}>
                <EmptyState
                    header=""
                    maxImageWidth={250}
                    description={formatMessage(message, {
                        b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                    })}
                    imageUrl={FallbackPortals}
                    width="wide"
                />
            </Box>
        );
    }
    if (!portals.length) return null;
    const defaultView = featuredPortals.length ? featuredPortals : allOtherPortals.slice(0, 9);
    const expandedView = featuredPortals.length ? allOtherPortals : allOtherPortals.slice(9);
    const remainingCount = portals.length - defaultView.length;
    const titleTextColor = (isIncreasedFlexibilityEnabled() && visualConfig?.titleColor) || undefined;
    const themeButtonColor = (isIncreasedFlexibilityEnabled() && expandButtonTextColor) || undefined;

    return (
        <AnalyticsContext
            data={{
                attributes: {
                    numberOfDefaultViewPortals: defaultView.length,
                    numberOfExpandedViewPortals: portals.length - defaultView.length,
                },
            }}
        >
            <ElementRoot elementWidth={ElementWidth.COMPACT}>
                {/* Single Portal should be handled as another element by help-aakar. We should do away with this
                    transformation on the client side.
                 */}
                {portals.length === 1 && portals[0]?.portalId ? (
                    <Box xcss={isIncreasedFlexibilityEnabled() && singlePortalStyles}>
                        <HelpCenterSinglePortal portalId={+portals[0]?.portalId} requestTypeSuggestion={[]} />
                    </Box>
                ) : (
                    <>
                        <Title textColor={titleTextColor}>
                            {featuredPortals.length
                                ? formatMessage(messages.featuredPortalsLabel)
                                : formatMessage(messages.remainingPortals)}
                        </Title>
                        <Stack xcss={defaultViewStyles}>
                            <Inline
                                as="ol"
                                shouldWrap
                                alignInline="center"
                                xcss={[portalsListStyles, isSidebarOpen ? portalsListStylesWhenSideBarOpen : undefined]}
                            >
                                {defaultView.map((portal) => {
                                    if (portal)
                                        return (
                                            <Inline as="li" xcss={portalListItemStyles} key={portal?.portalId}>
                                                <Portal portal={portal} />
                                            </Inline>
                                        );
                                    return null;
                                })}
                            </Inline>
                            {portals.length > defaultView.length && !areAllPortalsVisible && (
                                <ToggleViewButton
                                    id="explore-all-projects"
                                    overrideThemeColor={themeButtonColor}
                                    onClick={toggleExpandedView}
                                    appearance="link"
                                    iconAfter={<ChevronDownIcon label="" aria-hidden />}
                                    actionSubjectId="exploreAllProjects"
                                    aria-expanded={!areAllPortalsVisible}
                                    data-testId={'exploreAllProjects'}
                                    tabIndex={isPageInEditMode || isPageInPreviewMode ? -1 : undefined}
                                    nonClickable={isPageInEditMode || isPageInPreviewMode}
                                >
                                    <FormattedMessage
                                        defaultMessage={messages.exploreAll.defaultMessage}
                                        id={messages.exploreAll.id}
                                        values={{ remainingCount }}
                                    />
                                </ToggleViewButton>
                            )}
                        </Stack>
                        {areAllPortalsVisible && (
                            <Stack xcss={expandedViewStyles}>
                                <Title textColor={titleTextColor}>
                                    <FormattedMessage
                                        defaultMessage={messages.remainingProjectsByName.defaultMessage}
                                        id={messages.remainingProjectsByName.id}
                                    />
                                </Title>
                                <Inline
                                    as="ol"
                                    shouldWrap
                                    alignInline="center"
                                    xcss={[
                                        portalsListStyles,
                                        isSidebarOpen ? portalsListStylesWhenSideBarOpen : undefined,
                                    ]}
                                >
                                    {expandedView?.map((portal) => {
                                        if (portal)
                                            return (
                                                <Inline as="li" xcss={portalListItemStyles} key={portal?.portalId}>
                                                    <Portal portal={portal} />
                                                </Inline>
                                            );
                                        return null;
                                    })}
                                </Inline>
                                <ToggleViewButton
                                    id="collapse-other-projects"
                                    overrideThemeColor={themeButtonColor}
                                    onClick={toggleExpandedView}
                                    appearance="link"
                                    iconAfter={<ChevronUpIcon label="" aria-hidden />}
                                    actionSubjectId="collapseOtherProjects"
                                    aria-expanded={areAllPortalsVisible}
                                    data-testId={'collapseOtherProjects'}
                                >
                                    <FormattedMessage
                                        defaultMessage={messages.collapseOtherProjects.defaultMessage}
                                        id={messages.collapseOtherProjects.id}
                                    />
                                </ToggleViewButton>
                            </Stack>
                        )}
                    </>
                )}
            </ElementRoot>
        </AnalyticsContext>
    );
};

// @ts-ignore TS(7053) TypeScript upgrade 5.1.6, please fix this violation.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ToggleViewButton = styled(ThemedButton)<{ nonClickable?: boolean }>`
    && {
        ${fonts.h500};
        align-self: center;
        margin-top: ${token('space.0', '0px')};
        padding: ${token('space.100', '8px')};
        pointer-events: ${({ nonClickable }) => (nonClickable ? 'none' : 'auto')};
        &:hover {
            ${fonts.h500};
            margin-top: ${token('space.0', '0px')};
            padding: ${token('space.100', '8px')};
        }
    }
`;

// ref cardStyle in src/view/help-center/portal-card/styled.tsx
const portalCardWidth = '336px';

const portalsListStyles = xcss({
    paddingInline: 'space.0',
    marginBlock: 'space.0',
    rowGap: 'space.400',
    justifyContent: 'center',
    // @ts-ignore // 2-grid // because use using custom breakpoints
    '@media (min-width: 37.5rem)': {
        columnGap: `calc(100% - ${portalCardWidth} * 2)`,
    },
    // @ts-ignore // 3-grid // because use using custom breakpoints
    '@media (min-width: 80rem)': {
        columnGap: `calc((100% - ${portalCardWidth} * 3) / 2)`,
    },
});

const portalsListStylesWhenSideBarOpen = xcss({
    // @ts-ignore // 2-grid // because use using custom breakpoints
    '@media (min-width: 80rem)': {
        columnGap: `calc(100% - ${portalCardWidth} * 2)`,
    },
    // @ts-ignore // 3-grid // because use using custom breakpoints
    '@media (min-width: 102rem)': {
        // ref src/view/common/element-root/element-root.tsx:63
        columnGap: `calc((100% - ${portalCardWidth} * 3) / 2)`,
    },
});

const portalListItemStyles = xcss({
    marginBlock: 'space.0',
    maxWidth: '264px',
    // @ts-ignore // because use using custom breakpoints
    '@media (min-width: 37.5rem)': {
        maxWidth: portalCardWidth,
    },
});

const defaultViewStyles = xcss({ rowGap: 'space.300' });

const expandedViewStyles = xcss({ rowGap: 'space.300', marginTop: 'space.300' });

const nonDraggableStyles = xcss({
    pointerEvents: 'none',
});

const singlePortalStyles = xcss({
    //overflow is needed to prevent margin collapse
    overflow: 'auto',
    margin: '0 auto',
    borderRadius: 'radius.small',
    backgroundColor: 'elevation.surface.raised',
    boxShadow: 'elevation.shadow.raised',
    //This is the sum of loader height and bottom and top spacing
    minHeight: '96px',
    '@media (min-width: 90rem)': {
        width: '904px',
    },
});
